import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose,
} from "./ui/dialog";
import { Lock, Plus, Pencil, Check, X } from "lucide-react";

const Chk = ({ on }) => on ? <Check className="h-3.5 w-3.5 text-emerald-600 inline" /> : <X className="h-3.5 w-3.5 text-neutral-300 inline" />;

function Field({ label, value, onChange, type = "text", disabled, testid, placeholder }) {
  return (
    <label className="block text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input data-testid={testid} type={type} value={value ?? ""} placeholder={placeholder} disabled={disabled}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        className={`mt-1 w-full border border-input px-2 py-1.5 text-sm ${disabled ? "bg-neutral-100 text-muted-foreground" : "bg-white"}`} />
    </label>
  );
}
function Sel({ label, value, onChange, options, disabled, testid }) {
  return (
    <label className="block text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select data-testid={testid} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full border border-input px-2 py-1.5 text-sm ${disabled ? "bg-neutral-100" : "bg-white"}`}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function Toggle({ label, on, onChange, disabled, testid }) {
  return (
    <label className={`flex items-center gap-2 text-xs ${disabled ? "text-muted-foreground" : ""}`}>
      <input data-testid={testid} type="checkbox" checked={!!on} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}
const Group = ({ title, children }) => (
  <div className="border border-border p-3">
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
    <div className="grid grid-cols-2 gap-3">{children}</div>
  </div>
);

export default function PayrollCodesPanel() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [kind, setKind] = useState("earning");
  const [codes, setCodes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [diag, setDiag] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId(r.data[0].company_id); });
    api.get("/config/code-metadata").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/${kind}-codes`).then((r) => setCodes(r.data.codes || [])).catch(() => setCodes([]));
  }, [companyId, kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/code-diagnostics`).then((r) => setDiag(r.data)).catch(() => setDiag(null));
  }, [companyId, codes]);

  const openNew = () => {
    setEditing({ isNew: true });
    setForm(kind === "earning"
      ? { code: "", name: "", earning_category: "OTHER", taxable: true, cpp_pensionable: true, cpp2_applicable: true, ei_insurable: true, vacationable: true, overtime_eligible: false, statutory_holiday_eligible: false, roe_insurable_earnings: true, roe_insurable_hours: false, t4_box_code: "", default_rate_type: "MANUAL", gl_expense_account: "", active: true }
      : { code: "", name: "", deduction_category: "OTHER", employee_or_employer: "EMPLOYEE", calculation_basis: "FIXED_AMOUNT", pre_tax: false, reduces_taxable_income: false, reduces_cpp_pensionable_earnings: false, reduces_ei_insurable_earnings: false, t4_box_code: "", priority: 100, employer_match_enabled: false, gl_liability_account: "", gl_expense_account: "", active: true });
  };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      if (editing.isNew) {
        await api.post(`/companies/${companyId}/${kind}-codes`, form);
        toast.success("Code created");
      } else {
        await api.put(`/companies/${companyId}/${kind}-codes/${editing.code_id}`, form);
        toast.success(editing.system_code ? "Company override saved" : "Code updated");
      }
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleActive = async (c) => {
    try {
      if (c.active) { await api.delete(`/companies/${companyId}/${kind}-codes/${c.code_id}`); }
      else { await api.put(`/companies/${companyId}/${kind}-codes/${c.code_id}`, { active: true }); }
      toast.success(c.active ? "Deactivated" : "Activated"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sysProtected = editing?.system_code && !editing?.isNew;

  const resetOverride = async () => {
    const fields = kind === "earning" ? meta.earning_override_fields : meta.deduction_override_fields;
    try {
      await api.post(`/companies/${companyId}/${kind}-codes/${editing.code_id}/clear-override`, { fields });
      toast.success("Company overrides reset to global defaults");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="payroll-codes-panel">
      {diag && (
        <div data-testid="pc-diagnostics" className={`border mb-4 px-4 py-3 ${diag.healthy ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${diag.healthy ? "bg-emerald-500" : "bg-red-500"}`} />
            <span data-testid="pc-diag-status" className="font-bold text-sm">
              {diag.healthy ? "No code-integrity issues detected" : "Payroll code integrity issues detected"}
            </span>
            <span data-testid="pc-readiness" className={`ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${diag.ready_for_calc_integration ? "border-emerald-500 text-emerald-700" : "border-amber-500 text-amber-700"}`}>
              {diag.ready_for_calc_integration ? "Ready for calc integration" : "Not ready"}
            </span>
          </div>
          {diag.baseline_drift && (
            <div data-testid="pc-baseline-drift" className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Code baseline drift detected — live system codes differ from the locked {diag.baseline?.baseline_id}. Create a new baseline version if this change is intentional.
            </div>
          )}
          {diag.baseline?.found && !diag.baseline_drift && (
            <div className="mt-1 text-[10px] font-mono text-muted-foreground" data-testid="pc-baseline-info">Baseline {diag.baseline.baseline_id} · checksum {String(diag.baseline.checksum || "").slice(0, 12)} · matches live seed</div>
          )}
          {!diag.healthy && (
            <ul className="mt-2 text-xs text-red-700 space-y-1">
              {diag.issues.map((i, n) => (
                <li key={n} data-testid={`pc-diag-issue-${n}`}>• [{i.severity}] {i.type} {i.code ? `(${i.code})` : ""} — {i.detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select data-testid="pc-company-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="border border-input bg-white px-3 py-2 text-sm">
          {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
        </select>
        <div className="flex border border-border">
          {["earning", "deduction"].map((k) => (
            <button key={k} data-testid={`pc-kind-${k}`} onClick={() => setKind(k)} className={`px-4 py-2 text-sm font-medium ${kind === k ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>{k === "earning" ? "Earnings" : "Deductions"}</button>
          ))}
        </div>
        <button data-testid="pc-new-code" onClick={openNew} className="ml-auto inline-flex items-center gap-1.5 bg-black text-white px-3 py-2 text-sm hover:opacity-90"><Plus className="h-4 w-4" /> New custom code</button>
      </div>

      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="text-left">
              <th className="px-3 py-2">Code</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Category</th>
              {kind === "earning" ? <><th className="px-3 py-2 text-center">Tax</th><th className="px-3 py-2 text-center">CPP</th><th className="px-3 py-2 text-center">EI</th><th className="px-3 py-2 text-center">Vac</th><th className="px-3 py-2 text-center">T4</th></>
                : <><th className="px-3 py-2 text-center">Pre-tax</th><th className="px-3 py-2 text-center">↓Tax</th><th className="px-3 py-2 text-center">Match</th><th className="px-3 py-2 text-center">T4</th><th className="px-3 py-2 text-center">Prio</th></>}
              <th className="px-3 py-2">GL</th><th className="px-3 py-2 text-center">Active</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.code_id} data-testid={`pc-row-${c.code}`} className={`border-t border-border/60 ${c.active ? "" : "opacity-50"}`}>
                <td className="px-3 py-2 font-mono font-medium flex items-center gap-1">{c.code}{c.system_code && <Lock className="h-3 w-3 text-muted-foreground" title="System code" />}</td>
                <td className="px-3 py-2">{c.name}{c.has_company_override && <span className="ml-1 text-[9px] font-mono uppercase bg-sky-100 text-sky-700 px-1">override</span>}{c.requires_treatment_review && <span data-testid={`pc-review-${c.code}`} title="Treatment may depend on CRA circumstances" className="ml-1 text-[9px] font-mono uppercase bg-amber-100 text-amber-700 px-1">review</span>}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{c.earning_category || c.deduction_category}</td>
                {kind === "earning" ? <>
                  <td className="px-3 py-2 text-center"><Chk on={c.taxable} /></td>
                  <td className="px-3 py-2 text-center"><Chk on={c.cpp_pensionable} /></td>
                  <td className="px-3 py-2 text-center"><Chk on={c.ei_insurable} /></td>
                  <td className="px-3 py-2 text-center"><Chk on={c.vacationable} /></td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{c.t4_box_code || "—"}</td>
                </> : <>
                  <td className="px-3 py-2 text-center"><Chk on={c.pre_tax} /></td>
                  <td className="px-3 py-2 text-center"><Chk on={c.reduces_taxable_income} /></td>
                  <td className="px-3 py-2 text-center"><Chk on={c.employer_match_enabled} /></td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{c.t4_box_code || "—"}</td>
                  <td className="px-3 py-2 text-center text-xs tabular-nums">{c.priority}</td>
                </>}
                <td className="px-3 py-2 text-xs font-mono">{c.gl_expense_account || c.gl_liability_account || "—"}</td>
                <td className="px-3 py-2 text-center">
                  <button data-testid={`pc-toggle-${c.code}`} onClick={() => toggleActive(c)} className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${c.active ? "border-emerald-500 text-emerald-700" : "border-neutral-300 text-neutral-400"}`}>{c.active ? "Active" : "Off"}</button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`pc-edit-${c.code}`} onClick={() => openEdit(c)} className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs hover:border-black"><Pencil className="h-3 w-3" /> {c.system_code ? "Configure" : "Edit"}</button>
                </td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground text-sm">No codes.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none border-black sm:max-w-[760px] max-h-[92vh] overflow-y-auto" data-testid="pc-editor">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? `New ${kind} code` : `${editing?.code} — ${editing?.name}`}</DialogTitle>
            <DialogDescription>{sysProtected ? "System code — statutory treatment is protected. You may set company operational defaults (name, GL, active, dates)." : "Custom company code — configure treatment. Values will drive payroll only in later phases."}</DialogDescription>
          </DialogHeader>
          {editing && meta && (
            <div className="space-y-3">
              {(form.requires_treatment_review || editing.requires_treatment_review) && (
                <div data-testid="pc-review-note" className="border border-amber-400 bg-amber-50 text-amber-800 text-xs px-3 py-2">
                  Treatment may depend on CRA circumstances — verify taxability, CPP and EI for the specific benefit/allowance before relying on this code.
                </div>
              )}
              <Group title="General">
                <Field label="Code" value={form.code} onChange={set("code")} disabled={!editing.isNew} testid="pc-f-code" placeholder="e.g. SHIFT" />
                <Field label="Display name" value={form.name} onChange={set("name")} testid="pc-f-name" />
                {kind === "earning"
                  ? <Sel label="Category" value={form.earning_category} onChange={set("earning_category")} options={meta.earning_categories} disabled={sysProtected} testid="pc-f-category" />
                  : <Sel label="Category" value={form.deduction_category} onChange={set("deduction_category")} options={meta.deduction_categories} disabled={sysProtected} testid="pc-f-category" />}
                <Toggle label="Active" on={form.active} onChange={set("active")} testid="pc-f-active" />
              </Group>

              {kind === "earning" ? <>
                <Group title="Tax treatment (protected on system codes)">
                  <Toggle label="Income-tax taxable" on={form.taxable} onChange={set("taxable")} disabled={sysProtected} testid="pc-f-taxable" />
                  <Toggle label="CPP pensionable" on={form.cpp_pensionable} onChange={set("cpp_pensionable")} disabled={sysProtected} testid="pc-f-cpp" />
                  <Toggle label="CPP2 applicable" on={form.cpp2_applicable} onChange={set("cpp2_applicable")} disabled={sysProtected} />
                  <Toggle label="EI insurable" on={form.ei_insurable} onChange={set("ei_insurable")} disabled={sysProtected} testid="pc-f-ei" />
                </Group>
                <Group title="Payroll treatment">
                  <Toggle label="Vacationable" on={form.vacationable} onChange={set("vacationable")} disabled={sysProtected} testid="pc-f-vac" />
                  <Toggle label="Overtime eligible" on={form.overtime_eligible} onChange={set("overtime_eligible")} disabled={sysProtected} />
                  <Toggle label="Statutory-holiday eligible" on={form.statutory_holiday_eligible} onChange={set("statutory_holiday_eligible")} disabled={sysProtected} />
                  <Toggle label="ROE insurable earnings" on={form.roe_insurable_earnings} onChange={set("roe_insurable_earnings")} disabled={sysProtected} />
                  <Toggle label="ROE insurable hours" on={form.roe_insurable_hours} onChange={set("roe_insurable_hours")} disabled={sysProtected} />
                  <Sel label="Rate type" value={form.default_rate_type} onChange={set("default_rate_type")} options={meta.rate_types} disabled={sysProtected} />
                </Group>
                <Group title="Reporting (T4)">
                  <Field label="T4 box code" value={form.t4_box_code} onChange={set("t4_box_code")} disabled={sysProtected} testid="pc-f-t4box" placeholder="14" />
                  <Field label="T4 Other Information code" value={form.t4_other_information_code} onChange={set("t4_other_information_code")} disabled={sysProtected} />
                </Group>
              </> : <>
                <Group title="Calculation">
                  <Sel label="Basis" value={form.calculation_basis} onChange={set("calculation_basis")} options={meta.deduction_basis} disabled={sysProtected} testid="pc-f-basis" />
                  <Sel label="Employee / Employer" value={form.employee_or_employer} onChange={set("employee_or_employer")} options={["EMPLOYEE", "EMPLOYER"]} disabled={sysProtected} />
                  <Field label="Fixed amount" type="number" value={form.fixed_amount} onChange={set("fixed_amount")} />
                  <Field label="Percentage" type="number" value={form.percentage} onChange={set("percentage")} testid="pc-f-pct" />
                  <Field label="Per-pay max" type="number" value={form.per_pay_max} onChange={set("per_pay_max")} />
                  <Field label="Annual max" type="number" value={form.annual_max} onChange={set("annual_max")} />
                  <Field label="Priority (lower = first)" type="number" value={form.priority} onChange={set("priority")} testid="pc-f-priority" />
                </Group>
                <Group title="Tax treatment (protected on system codes)">
                  <Toggle label="Pre-tax" on={form.pre_tax} onChange={set("pre_tax")} disabled={sysProtected} testid="pc-f-pretax" />
                  <Toggle label="Reduces taxable income" on={form.reduces_taxable_income} onChange={set("reduces_taxable_income")} disabled={sysProtected} />
                  <Toggle label="Reduces CPP base" on={form.reduces_cpp_pensionable_earnings} onChange={set("reduces_cpp_pensionable_earnings")} disabled={sysProtected} />
                  <Toggle label="Reduces EI base" on={form.reduces_ei_insurable_earnings} onChange={set("reduces_ei_insurable_earnings")} disabled={sysProtected} />
                </Group>
                <Group title="Employer match">
                  <Toggle label="Enable employer match" on={form.employer_match_enabled} onChange={set("employer_match_enabled")} testid="pc-f-match" />
                  <Sel label="Match type" value={form.employer_match_type} onChange={set("employer_match_type")} options={["", ...meta.employer_match_types]} disabled={!form.employer_match_enabled} />
                  <Field label="Match rate" type="number" value={form.employer_match_rate} onChange={set("employer_match_rate")} disabled={!form.employer_match_enabled} />
                  <Field label="Match max" type="number" value={form.employer_match_max} onChange={set("employer_match_max")} disabled={!form.employer_match_enabled} />
                </Group>
                <Group title="Reporting (T4)">
                  <Field label="T4 box code" value={form.t4_box_code} onChange={set("t4_box_code")} disabled={sysProtected} />
                  <Field label="T4 Other Information code" value={form.t4_other_information_code} onChange={set("t4_other_information_code")} disabled={sysProtected} />
                </Group>
              </>}
              <Group title="Accounting (GL) & effective dates">
                <Field label="GL expense account" value={form.gl_expense_account} onChange={set("gl_expense_account")} testid="pc-f-gl-exp" />
                <Field label="GL liability account" value={form.gl_liability_account} onChange={set("gl_liability_account")} />
                <Field label="Effective from" type="date" value={form.effective_from} onChange={set("effective_from")} />
                <Field label="Effective to" type="date" value={form.effective_to} onChange={set("effective_to")} />
              </Group>
            </div>
          )}
          <DialogFooter>
            {sysProtected && editing?.has_company_override && (
              <button data-testid="pc-reset-override" onClick={resetOverride} className="mr-auto border border-amber-500 text-amber-700 px-4 py-2 text-sm hover:bg-amber-50">Reset to global defaults</button>
            )}
            <DialogClose asChild><button className="border border-border px-4 py-2 text-sm hover:border-black">Cancel</button></DialogClose>
            <button data-testid="pc-save" onClick={save} className="bg-black text-white px-4 py-2 text-sm hover:opacity-90">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
